/**
 * Formatting utilities for dates, prices, and numbers
 */

/**
 * Format a date to a localized date string
 * @param {string|Date} date - Date string or Date object
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString();
};

/**
 * Format a price to 2 decimal places with dollar sign
 * @param {number} price - Price value
 * @returns {string} Formatted price string (e.g., "$185.50")
 */
export const formatPrice = (price) => {
  if (price === null || price === undefined) return 'N/A';
  return `$${Number(price).toFixed(2)}`;
};

/**
 * Format a percentage change with sign
 * @param {number} change - Change value
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted change string (e.g., "+2.35%")
 */
export const formatChange = (change, decimals = 2) => {
  if (change === null || change === undefined) return 'N/A';
  const sign = change >= 0 ? '+' : '';
  return `${sign}${change.toFixed(decimals)}`;
};

/**
 * Format a percentage with sign
 * @param {number} percent - Percentage value
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted percentage string (e.g., "+1.28%")
 */
export const formatPercent = (percent, decimals = 2) => {
  if (percent === null || percent === undefined) return 'N/A';
  const sign = percent >= 0 ? '+' : '';
  return `${sign}${percent.toFixed(decimals)}%`;
};

/**
 * Format market cap in shorthand (T/B/M/K)
 * @param {number} marketCap - Market cap value (should be in raw dollars)
 * @returns {string} Formatted market cap string (e.g., "1.2T", "554.53B", "125.4M", "5.2K")
 */
export const formatMarketCap = (marketCap) => {
  if (!marketCap || marketCap === 0) return 'N/A';
  
  // Ensure we have a number
  const numValue = typeof marketCap === 'number' ? marketCap : Number(marketCap);
  if (isNaN(numValue) || !isFinite(numValue)) return 'N/A';
  
  let absValue = Math.abs(numValue);
  const originalValue = absValue;
  
  // Market cap values in the database may be stored in EITHER millions OR raw dollars
  // We need to detect which format and convert to raw dollars for proper formatting
  // 
  // Detection logic:
  // - Values >= 1e12 (1 trillion) are likely already in raw dollars
  // - Values < 1e6 (1 million) are likely in millions (need conversion)
  // - Values between 1e6 and 1e12 could be either, but most market caps in this range
  //   would be in millions (e.g., 53,700 million = 53.7B, not 53.7T in raw dollars)
  //
  // Examples:
  // - 8,051,684.04 (millions) = 8,051,684,040,000 = 8.05T
  // - 10,036,750.08 (millions) = 10,036,750,080,000 = 10.04T
  // - 53,700 (millions) = 53,700,000,000 = 53.70B
  // - 10.04 (millions) = 10,040,000 = 10.04M
  // - 53,702,600,000,000 (raw dollars) = 53.70T (already correct)
  
  // Convert from millions to raw dollars if the value looks like it's in millions
  // Detection logic based on magnitude:
  // - Values >= 1e9 (1 billion) are likely already in raw dollars
  //   (e.g., 19,967,311,872 = 19.97B in raw dollars, 3,562,567,892,992 = 3.56T in raw dollars)
  // - Values < 1e9 are likely in millions and need conversion
  //   (e.g., 953,435.62 million = 953.44B, 12,524.92 million = 12.52B)
  // The largest realistic market cap in millions would be around 1,000,000 million (1T = 1e6)
  if (absValue < 1e9) {
    // Value is < 1 billion, likely in millions, convert to raw dollars
    absValue = absValue * 1e6;
  }
  // If value is >= 1e9, assume it's already in raw dollars
  
  if (absValue >= 1e12) {
    // Trillions
    const trillions = absValue / 1e12;
    // Show 1 decimal place if < 10, otherwise show 2 decimal places
    const decimals = trillions < 10 ? 1 : 2;
    return `${trillions.toFixed(decimals)}T`;
  } else if (absValue >= 1e9) {
    // Billions
    const billions = absValue / 1e9;
    // Show 1 decimal place if < 10, otherwise show 2 decimal places
    const decimals = billions < 10 ? 1 : 2;
    return `${billions.toFixed(decimals)}B`;
  } else if (absValue >= 1e6) {
    // Millions
    const millions = absValue / 1e6;
    // Show 1 decimal place if < 10, otherwise show 2 decimal places
    const decimals = millions < 10 ? 1 : 2;
    return `${millions.toFixed(decimals)}M`;
  } else if (absValue >= 1e3) {
    // Thousands
    const thousands = absValue / 1e3;
    return `${thousands.toFixed(1)}K`;
  } else {
    // Less than 1000, show as is
    return `${absValue.toFixed(0)}`;
  }
};

/**
 * Format confidence as percentage
 * @param {number} confidence - Confidence value (0-1)
 * @returns {string} Formatted confidence string (e.g., "85%")
 */
export const formatConfidence = (confidence) => {
  if (confidence === null || confidence === undefined) return 'N/A';
  return `${(confidence * 100).toFixed(0)}%`;
};
